import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown, Sparkles } from "lucide-react";
import { DropdownMenu } from "radix-ui";

import { platformRole, type NavGroup } from "@/app/registry";
import { useSession } from "@/app/session";
import { cn } from "@/lib/cn";
import { ADMIN_GROUPS } from "@/pages/admin/sections";

import { isConsolePath, useAdminSections } from "./consolePages";
import { highlighted, sectionOf, useSectionLabels, useSections } from "./sections";

const itemClass =
  "flex items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-[13.5px] text-fg outline-none data-[highlighted]:bg-surface-2";

/**
 * The top-menu layout's sections: each opens a panel with its pages (the
 * admin's panel lists every admin console page in columns).
 */
export function TopMenu() {
  const sections = useSections();
  const labels = useSectionLabels();
  const { user } = useSession();
  const admin = platformRole(user?.role) === "admin";
  const adminPages = useAdminSections();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const current = isConsolePath(path) ? "System" : sectionOf(sections, path);

  return (
    <nav aria-label="Main" className="hidden h-full min-w-0 items-center gap-0.5 lg:flex 2xl:gap-1">
      {[...sections.entries()].map(([group, entries]) => {
        const on = group === current;
        const mega = admin && group === "System";
        return (
          <DropdownMenu.Root key={group} modal={false}>
            <DropdownMenu.Trigger
              className={cn(
                // one line each; tighter until 2xl so every section fits a 1440 px laptop
                "inline-flex h-9 items-center gap-1 rounded-[10px] px-2 text-sm font-medium whitespace-nowrap outline-none transition-colors 2xl:px-3",
                on
                  ? "bg-white/10 text-white"
                  : "text-[#c6cfeb] hover:bg-white/[0.06] hover:text-white",
                "data-[state=open]:bg-white/10 data-[state=open]:text-white",
              )}
            >
              {highlighted(entries) ? <Sparkles className="size-3.5 text-[#c4b5ff]" aria-hidden /> : null}
              {labels[group as NavGroup]}
              <ChevronDown className="hidden size-3.5 opacity-70 2xl:block" aria-hidden />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="start"
                sideOffset={10}
                className={cn(
                  "z-50 rounded-[18px] border border-border bg-surface p-3 shadow-[0_30px_60px_-20px_rgb(10_18_48/0.45)]",
                  mega
                    ? "grid w-[min(860px,calc(100vw-2rem))] grid-cols-4 gap-2"
                    : "grid min-w-64 gap-0.5",
                )}
              >
                {mega
                  ? ADMIN_GROUPS.map((g) => {
                      const items = adminPages.filter((s) => s.group === g);
                      if (!items.length) return null;
                      return (
                        <div key={g}>
                          <div className="px-2.5 pt-1 pb-1.5 text-[10.5px] font-semibold tracking-[0.09em] text-subtle uppercase">
                            {g === "Overview" ? "Console" : g}
                          </div>
                          {items.map((s) => {
                            const Icon = s.icon;
                            return (
                              <DropdownMenu.Item key={s.id} asChild>
                                <Link
                                  to={s.path as never}
                                  className={itemClass}
                                >
                                  <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-surface-2 text-muted">
                                    <Icon className="size-4" />
                                  </span>
                                  <span className="truncate font-medium">
                                    {s.label}
                                  </span>
                                </Link>
                              </DropdownMenu.Item>
                            );
                          })}
                        </div>
                      );
                    })
                  : null}
                {(mega
                  ? entries.filter((p) => p.path !== "/app/admin")
                  : entries
                ).map((p) => {
                  const Icon = p.nav.icon;
                  const Count = p.nav.badge;
                  return (
                    <DropdownMenu.Item key={p.path} asChild>
                      <Link
                        to={p.path}
                        className={cn(itemClass, mega && "col-span-1")}
                      >
                        <span
                          className={cn(
                            "grid size-7 shrink-0 place-items-center rounded-lg",
                            p.nav.highlight ? "bg-tint-violet text-[#6d4fd8] dark:text-[#b39dff]" : "bg-surface-2 text-muted",
                          )}
                        >
                          <Icon className="size-4" />
                        </span>
                        <span className="truncate font-medium">
                          {p.nav.label}
                        </span>
                        {Count ? (
                          <span className="ml-auto">
                            <Count />
                          </span>
                        ) : null}
                      </Link>
                    </DropdownMenu.Item>
                  );
                })}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        );
      })}
    </nav>
  );
}
